import React from 'react';
import {
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
  CircularProgress,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import {
  CancelRounded,
  PlayArrowRounded,
  Favorite,
  FavoriteBorder,
  ShoppingBasket,
  ShoppingCart,
} from '@mui/icons-material';
import { ICourseData } from '../../types/course-data';
import { ModalDescription } from './modal-description';
import { LessonList } from './modal-lesson-list';
import { ModalMetadata } from './modal-metadata';
import { ModalVideo } from './modal-video';
import { DialogContentMobile } from './modal-content-mobile';
import { CourseType } from '../../types/course';
import { routes } from 'src/shared/utils/routes';
import { useCatalogFavourite } from '../../hooks/use-catalog-favourite';
import { CatalogEvents } from '../../events';
import { EventBus } from '@/shared/utils';
import { useUserStatus } from '../../context/user-status-context';
import '../../styles/custom-scrollbar.css';
import { adaptCourseTypeText } from 'src/shared/utils/adapt-course-type-text';
interface ModalDetailsProps extends ICourseData {
  dialogIsOpen: boolean;
  dialogToggle: () => void;
  trailerUrl: string;
  viewLessonCallback: (title: string) => void;
  favouriteId: string;
  eventBus: EventBus<CatalogEvents>;
  colorScheme: any;
  seasons?: any;
  onFavoriteChange?: (courseId: string, favouriteId: string | undefined) => void;
}

export const ModalDetails = ({
  documentId,
  title,
  description,
  videoCount,
  duration,
  level,
  genres,
  instructors,
  trailerPlaybackId,
  trailerPlaybackToken,
  dialogIsOpen,
  dialogToggle,
  lessons,
  coursePercentage,
  imageSrc,
  colorScheme,
  courseType,
  viewLessonCallback,
  favouriteId,
  isPurchasable,
  eventBus,
  seasons,
  onFavoriteChange,
}: ModalDetailsProps) => {
  // Map course type to document type for favorites
  const getDocumentType = (type?: string): 'serie' | 'masterclass' | 'learningPath' => {
    if (!type) return 'serie';

    switch (type?.toLowerCase()) {
      case 'serie':
        return 'serie';
      case 'masterclass':
        return 'masterclass';
      case 'percorsi':
        return 'learningPath';
      default:
        return 'serie';
    }
  };
  const { isLoggedIn } = useUserStatus();
  const documentType = getDocumentType(courseType);

  // Use the favorite hook with the callback
  const { isFavourite, isLoading, toggleFavourite } = useCatalogFavourite({
    eventBus,
    documentId,
    documentType,
    favouriteId,
    onFavouriteChange: onFavoriteChange, // Pass the callback through
  });

  const handleRedirect = (type?: CourseType) => {
    if (!type) return '';

    if (type?.toLowerCase() === 'masterclass') {
      return routes.masterclass.makeUrl(documentId, title);
    }
    if (type?.toLowerCase() === 'serie') {
      return routes.seriesDetails.makeUrl(documentId, title);
    }
    return routes.learningPathDetails.makeUrl(documentId, title);
  };

  // Handle lesson navigation
  const handleLessonSelect = (url: string) => {
    if (url && viewLessonCallback) {
      viewLessonCallback(url);
    }
  };

  return (
    <Dialog
      maxWidth="md"
      open={dialogIsOpen}
      onClose={dialogToggle}
      fullWidth
      className="custom-scrollbar"
    >
      <DialogTitle
        variant="h5"
        component="h5"
        className="relative flex justify-between text-text-primary"
      >
        <span className="hidden md:block">{title}</span>

        <div className="h-[24px] w-[24px] md:hidden" />
        <Chip
          label={<Typography variant="button-s">{adaptCourseTypeText(courseType)}</Typography>}
          size="small"
          sx={{
            color: colorScheme.courseTypeTag.text,
            backgroundColor: colorScheme.courseTypeTag.bg,
          }}
          variant="filled"
          className="flex m-auto md:hidden"
        />
        <IconButton
          color="primary"
          aria-label="close"
          onClick={dialogToggle}
          sx={(theme) => ({
            color: theme.palette.action.divider,
            [theme.breakpoints.up('lg')]: {
              position: 'absolute',
              right: 16,
              top: 16,
            },
          })}
        >
          <CancelRounded sx={{ fontSize: 32 }} />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <ModalVideo
          trailerPlaybackId={trailerPlaybackId}
          trailerPlaybackToken={trailerPlaybackToken}
          placeholderImg={imageSrc || '/assets/images/card-placeholder.png'}
        />
        <div className="hidden md:block">
          <Grid container spacing={4}>
            <Grid size={{ xs: 12, md: 8 }}>
              <ModalDescription
                description={description}
                progressValue={coursePercentage}
                tagColor={colorScheme.courseTypeTag}
                tagText={courseType}
              />
              {courseType?.toLowerCase() !== 'masterclass' && (
                <LessonList
                  lessonCount={videoCount || 0}
                  lessons={lessons || []}
                  courseType={courseType}
                  courseTitle={title}
                  courseDocumentId={documentId}
                  onLessonSelect={handleLessonSelect}
                  seasons={courseType?.toLowerCase() === 'serie' ? seasons : undefined}
                />
              )}
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <ModalMetadata
                key={Math.random()}
                videoCount={videoCount}
                level={level}
                genres={genres}
                duration={duration}
                instructors={instructors}
                courseType={courseType}
                highlighted={undefined}
                isFavourite={isFavourite}
                isLoading={isLoading}
                toggleFavourite={toggleFavourite}
                isLoggedIn={isLoggedIn}
              />
            </Grid>
          </Grid>
        </div>
        <div className="md:hidden">
          <DialogContentMobile
            description={description}
            progressValue={coursePercentage}
            title={title}
            key={Math.random()}
            videoCount={videoCount}
            level={level}
            genres={genres}
            duration={duration}
            instructors={instructors}
            courseType={courseType}
            highlighted={false}
            isFavourite={isFavourite}
            isLoading={isLoading}
            toggleFavourite={toggleFavourite}
            isLoggedIn={isLoggedIn}
          >
            {courseType?.toLowerCase() !== 'masterclass' && (
              <LessonList
                lessonCount={videoCount || 0}
                lessons={lessons || []}
                courseType={courseType}
                courseTitle={title}
                courseDocumentId={documentId}
                onLessonSelect={handleLessonSelect}
                seasons={courseType?.toLowerCase() === 'serie' ? seasons : undefined}
              />
            )}
          </DialogContentMobile>
        </div>
      </DialogContent>
      <DialogActions>
        {/* {!isLoggedIn && courseType.toLowerCase() === 'serie' && isPurchasable && (
          <Button
            size="medium"
            variant="contained"
            color="secondary"
            className="self-stretch w-full my-auto md:w-auto"
            onClick={() => viewLessonCallback('/auth/login')}
          >
            <ShoppingCart />
            <Typography variant="button-m">Acquista serie singola</Typography>
          </Button>
        )} */}
        <Button
          size="medium"
          variant="contained"
          color="primary"
          className="self-stretch w-full my-auto md:w-auto"
          onClick={() =>
            viewLessonCallback(
              !isLoggedIn ? '/auth/registrazione' : handleRedirect(courseType as CourseType),
            )
          }
        >
          <PlayArrowRounded />
          <Typography variant="button-m">
            {!isLoggedIn ? 'Inizia a guardare gratis' : 'Guarda ora'}
          </Typography>
        </Button>
      </DialogActions>
    </Dialog>
  );
};
